import { Navigate, RouteObject } from 'react-router';
import { createBrowserRouter } from 'react-router-dom';

import { NotFoundElement } from '../../components/NotFoundElement.tsx';
import { OrderErrorBoundary } from '../../components/OrderErrorBoundary.tsx';
import { ErrorBoundaryLayout } from '../../layout/ErrorBoundaryLayout.tsx';
import { MarketAdjustmentsModule } from '../../modules/market/MarketAdjustmentsModule.tsx';
import { ToolkitController } from '../ToolkitController.tsx';
import { AccountIDRouteElement } from './AccountIDRouteElement.tsx';
import { AccountRouteElement } from './AccountRouteElement.tsx';
import { AdjustmentsGridRouteElement } from './AdjustmentsGridRouteElement.tsx';
import { AdjustmentsRouteElement } from './AdjustmentsRouteElement.tsx';
import { AdminGuard } from './AdminGuard.tsx';
import { AdminRouteElement } from './AdminRouteElement.tsx';
import { AnalysisRouteElement } from './AnalysisRouteElement.tsx';
import { AppraisifyCommentaryRouteElement } from './AppraisifyCommentaryRouteElement.tsx';
import { CommentaryRouteElement } from './CommentaryRouteElement.tsx';
import { CostApproachRouteElement } from './CostApproachRouteElement.tsx';
import { CraftsmanAlertsSheetRouteElement } from './CraftsmanAlertsSheetRouteElement.tsx';
import { FeatureGridRouteElement } from './FeatureGridRouteElement.tsx';
import { ImageAnalysisDemoRouteElement } from './ImageAnalysisDemoRouteElement.tsx';
import { NotSupportedElement } from './NotSupportedElement.tsx';
import { OnboardingRouteElement } from './OnboardingRouteElement.tsx';
import { OrderDetailsRouteElement } from './OrderDetailsRouteElement.tsx';
import { OrderRootElement } from './OrderRootElement.tsx';
import { PageGuidePopoutRouteElement } from './PageGuidePopoutRouteElement.tsx';
import { PairedSalesStandaloneRouteElement } from './PairedSalesStandaloneRouteElement.tsx';
import { PrepareOrderElement } from './PrepareOrderElement.tsx';
import { QARouteElement } from './QARouteElement.tsx';
import { ReconciliationRouteElement } from './ReconciliationRouteElement.tsx';
import { RootStudiesElement } from './RootStudiesElement.tsx';
import { StudiesRouteElement } from './StudiesRouteElement.tsx';
import { SubscribeRouteElement } from './SubscribeRouteElement.tsx';
import { SubscriptionGuard } from './SubscriptionGuard.tsx';
import { Tkv2LegacyOrderExportRouteElement } from './Tkv2LegacyOrderExportRouteElement.tsx';
import { Tkv2MlsDataSheetRouteElement } from './Tkv2MlsDataSheetRouteElement.tsx';
import { Tkv2MlsImageCarouselRouteElement } from './Tkv2MlsImageCarouselRouteElement.tsx';
import { Tkv2MlsPropertyMapRouteElement } from './Tkv2MlsPropertyMapRouteElement.tsx';
import { Tkv2MlsPropertyStreetViewRouteElement } from './Tkv2MlsPropertyStreetViewRouteElement.tsx';
import { Tkv2OrdersListRouteElement } from './Tkv2OrdersListRouteElement.tsx';
import { Tkv2PublicRecordSheetRouteElement } from './Tkv2PublicRecordSheetRouteElement.tsx';
import { Tkv2ReferenceViewRouteElement } from './Tkv2ReferenceViewRouteElement.tsx';
import { Tkv2SiteValueRouteElement } from './Tkv2SiteValueRouteElement.tsx';
import { InspectifyGuard } from './InspectifyGuard.tsx';
import { AppraisifyAnalysisRouteElement } from './AppraisifyAnalysisRouteElement.tsx';

function requireInspectify(...routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => ({
    ...route,
    element: <InspectifyGuard>{route.element}</InspectifyGuard>,
  }));
}

const notFoundRoute: RouteObject = {
  path: '*',
  element: <NotFoundElement />,
};

const accountIDRoute: RouteObject = {
  path: 'account-id',
  element: <AccountIDRouteElement />,
};

const accountRoute: RouteObject = {
  path: 'account',
  element: <AccountRouteElement />,
};

const adminRoute: RouteObject = {
  path: 'admin',
  element: <AdminRouteElement />,
};

const landingRoute: RouteObject = {
  path: '',
  element: <Navigate to="/orders" replace />,
};

const notSupportedFormRoute: RouteObject = {
  path: 'not-supported-form',
  element: <NotSupportedElement />,
};

const tkv2LegacyExportRoute: RouteObject = {
  path: 'legacy-export',
  element: <Tkv2LegacyOrderExportRouteElement />,
};

const featureGridRoute: RouteObject = {
  path: 'features',
  element: <FeatureGridRouteElement />,
};

const marketAdjustmentsRoute: RouteObject = {
  path: 'market',
  element: <MarketAdjustmentsModule />,
};

const analysisRoute: RouteObject = {
  path: 'analysis',
  element: <AnalysisRouteElement />,
};

const costApproachRoute: RouteObject = {
  path: 'cost-approach',
  element: <AppraisifyAnalysisRouteElement />,
}

const craftsmanAdjustmentsRoute: RouteObject = {
  path: 'adjustments',
  element: <AdjustmentsRouteElement />,
};

const adjustmentsGridRoute: RouteObject = {
  path: 'adjustments-grid',
  element: <AdjustmentsGridRouteElement />,
};

const commentaryRoute: RouteObject = {
  path: 'commentary',
  element: <CommentaryRouteElement />,
};

const appraisifyCommentaryRoute: RouteObject = {
  path: 'appraisify-commentary',
  element: <AppraisifyCommentaryRouteElement />,
};

const pairedCostRoute: RouteObject = {
  path: 'craftsman-cost-approach',
  element: <CostApproachRouteElement />,
};

const orderDetailsRoute: RouteObject = {
  path: 'details',
  element: <OrderDetailsRouteElement />,
};

const reconciliationRoute: RouteObject = {
  path: 'reconciliation',
  element: <ReconciliationRouteElement />,
};

const tkv2PairedCostRoute: RouteObject = {
  path: 'paired-cost',
  children: [
    marketAdjustmentsRoute,
    analysisRoute,
    costApproachRoute,
    craftsmanAdjustmentsRoute,
    commentaryRoute,
    pairedCostRoute,
    ...requireInspectify(
      orderDetailsRoute,
      featureGridRoute,
      adjustmentsGridRoute,
      appraisifyCommentaryRoute,
      reconciliationRoute,
    )
  ],
};

const tkv2PairedSalesStandaloneRoute: RouteObject = {
  path: 'paired-sales/:studyID?',
  element: <PairedSalesStandaloneRouteElement />,
};

const tkv2StudiesRoute: RouteObject = {
  path: 'studies',
  element: <RootStudiesElement />,
  children: [{ element: <StudiesRouteElement />, index: true }, tkv2PairedSalesStandaloneRoute],
};

const qcRoute: RouteObject = {
  path: 'qc',
  element: <QARouteElement />,
};

const orderRootRoute: RouteObject = {
  path: 'order/:orderId',
  element: <OrderRootElement />,
  errorElement: <OrderErrorBoundary />,
  children: [
    tkv2LegacyExportRoute,
    tkv2PairedCostRoute,
    tkv2StudiesRoute,
    qcRoute,
    notFoundRoute,
    analysisRoute,
  ],
};

const prepareOrderRoute: RouteObject = {
  path: 'prepareOrder/:orderId',
  element: <PrepareOrderElement />,
};

const tkv2OrdersListRoute: RouteObject = {
  path: 'orders',
  element: <Tkv2OrdersListRouteElement />,
};

const tkv2ArchivedOrdersListRoute: RouteObject = {
  path: 'archived-orders',
  element: <Tkv2OrdersListRouteElement />,
};

const tkv2CompletedOrdersListRoute: RouteObject = {
  path: 'completed-orders',
  element: <Tkv2OrdersListRouteElement />,
};

const tkv2TrainingOrdersListRoute: RouteObject = {
  path: 'training-orders',
  element: <Tkv2OrdersListRouteElement />,
};

const alertsRoute: RouteObject = {
  // TODO: I dont think we need an active ID but leaving it while i develop this
  path: 'craftsman-alerts/:activeID',
  element: <CraftsmanAlertsSheetRouteElement />,
};

const tkv2MlsListingRoute: RouteObject = {
  path: 'mls-listing/:activeID',
  element: <Tkv2MlsDataSheetRouteElement />,
};

const tkv2MlsImageRoute: RouteObject = {
  path: 'mls-images/:activeID',
  element: <Tkv2MlsImageCarouselRouteElement />,
};

const tkv2MlsMapRoute: RouteObject = {
  path: 'mls-map/:activeID',
  element: <Tkv2MlsPropertyMapRouteElement />,
};

const tkv2MlsStreetViewRoute: RouteObject = {
  path: 'mls-street-view/:activeID',
  element: <Tkv2MlsPropertyStreetViewRouteElement />,
};

const tkv2PublicRecordSheetRoute: RouteObject = {
  path: 'public-record/:activeID',
  element: <Tkv2PublicRecordSheetRouteElement />,
};

const tkv2SiteDataChartsRoute: RouteObject = {
  path: 'site-analysis/:activeID',
  element: <Tkv2SiteValueRouteElement />,
};

const tkv2ReferenceViewRoute: RouteObject = {
  path: 'reference-data/:orderId/:orderType',
  element: <Tkv2ReferenceViewRouteElement />,
  children: [
    alertsRoute,
    tkv2MlsListingRoute,
    tkv2MlsImageRoute,
    tkv2MlsMapRoute,
    tkv2MlsStreetViewRoute,
    tkv2PublicRecordSheetRoute,
    tkv2SiteDataChartsRoute,
  ],
};

const pageGuideRoute: RouteObject = {
  path: 'page-guide/:pageID',
  element: <PageGuidePopoutRouteElement />,
};

const onboardingRoute: RouteObject = {
  path: 'onboarding',
  element: <OnboardingRouteElement />,
};

const subscribeRoute: RouteObject = {
  path: 'subscribe',
  element: <SubscribeRouteElement />,
};

const imageAnalysisDemoRoute: RouteObject = {
  path: 'image-analysis-demo',
  element: <ImageAnalysisDemoRouteElement />,
};

function requireSubscription(...routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => ({
    ...route,
    element: <SubscriptionGuard>{route.element}</SubscriptionGuard>,
  }));
}

function requireAdmin(...routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => ({
    ...route,
    element: <AdminGuard>{route.element}</AdminGuard>,
  }));
}


const rootRoute: RouteObject = {
  path: '',
  element: <ToolkitController />,
  children: [
    ...requireSubscription(
      landingRoute,
      accountRoute,
      accountIDRoute,
      orderRootRoute,
      tkv2OrdersListRoute,
      tkv2ArchivedOrdersListRoute,
      tkv2TrainingOrdersListRoute,
      tkv2CompletedOrdersListRoute,
      notFoundRoute,
      tkv2ReferenceViewRoute,
      pageGuideRoute,
      notSupportedFormRoute,
      prepareOrderRoute,
      ...requireAdmin(adminRoute),
    ),
    onboardingRoute,
    subscribeRoute,
    imageAnalysisDemoRoute,
  ],
};

/**
 * When implementing the route tree, any route object containing children is
 * called a "root route" and should have the "RootRoute" suffix
 */
export const router = createBrowserRouter([
  { element: <ErrorBoundaryLayout />, children: [rootRoute] },
]);
